<template>
  <div
    class="tw-flex tw-flex-col md:tw-flex-row tw-gap-[8px] md:tw-gap-[18px] tw-items-start md:tw-items-center tw-max-w-[1280px] tw-px-[16px] md:tw-px-[24px]"
  >
    <p class="tw-hidden md:tw-block tw-mb-0 tw-text-[18px] tw-text-[var(--my-white-soft)]">
      {{ $t("donor_dashboard.my_contacts_fixed_bar_text", "Change all selected statuses to") }}
    </p>
    <p class="tw-block md:tw-hidden tw-mb-0 tw-text-[16px] tw-text-[var(--my-white-soft)]">
      {{ $t("donor_dashboard.my_contacts_fixed_bar_mobile_text", "Click to change statuses") }}
    </p>
    <div class="tw-flex tw-gap-[34px] md:tw-gap-[22px]">
      <my-dropdown variant="my-status-select" @set-status="setStatus" />

      <div class="tw-flex tw-gap-[14px]">
        <contact-list-item-link
          v-if="selectedPhones.length > 0"
          :data="selectedPhones"
          type="sms"
          class="tw-w-[40px] tw-h-[40px] md:tw-w-[47px] md:tw-h-[47px]"
        />
        <contact-list-item-link
          v-if="selectedEmails.length > 0"
          :data="selectedEmails"
          type="email"
          class="tw-w-[40px] tw-h-[40px] md:tw-w-[47px] md:tw-h-[47px]"
        />
        <button
          v-if="selectedContacts.length > 0"
          @click="deleteSelectedContacts"
          :disabled="loadingBulkDelete"
          class="tw-w-[40px] tw-h-[40px] md:tw-w-[47px] md:tw-h-[47px] tw-flex tw-items-center tw-justify-center tw-bg-[var(--my-white-soft)] tw-rounded-full tw-text-[var(--my-text-color)] hover:tw-text-[var(--my-primary)] tw-transition-colors"
          title="Delete Selected Contacts"
        >
          <b-icon-trash
            v-if="!loadingBulkDelete"
            class="tw-w-[22px] tw-h-[22px] tw-text-[var(--my-text-color)]"
          />
          <b-spinner v-else size="sm" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import contactListItemLink from "@/views/donor-dashboard/my-contacts/contact-list/contact-list-item/contact-list-item-link.vue";
import myDropdown from "@/views/donor-dashboard/my-contacts/ui/my-dropdown.vue";
import { useTeamLeaderContacts } from "@/composables/useTeamLeaderContacts";
import { notificationsMixin } from "@/mixins";
import { mapState, mapActions } from "vuex";

export default {
  components: {
    contactListItemLink,
    myDropdown,
  },

  mixins: [notificationsMixin],

  data() {
    return {
      isMobile: false,
      loadingBulkStatus: false,
      loadingBulkDelete: false,
    };
  },

  computed: {
    ...mapState({
      selectedContacts: (state) => state.donorMyContacts.selectedContacts,
    }),
    teamLeaderContacts() {
      // useTeamLeaderContacts composable exposes contacts as a computed property
      return this.$options.setup().contacts.value;
    },
    selectedPhones() {
      // Map selected contact IDs to their phone numbers
      return this.teamLeaderContacts
        .filter((contact) => this.selectedContacts.includes(contact.id))
        .map((contact) => contact.phone)
        .filter(Boolean);
    },
    selectedEmails() {
      // Map selected contact IDs to their emails
      return this.teamLeaderContacts
        .filter((contact) => this.selectedContacts.includes(contact.id))
        .map((contact) => contact.email)
        .filter(Boolean);
    },
  },

  setup() {
    const { updateContactStatus, fetchContacts, contacts, deleteContact } = useTeamLeaderContacts();

    return {
      updateContactStatus,
      fetchContacts,
      contacts,
      deleteContact,
    };
  },

  methods: {
    ...mapActions({
      selectedMyContactsHandler: "donorMyContacts/selectedMyContactsHandler",
    }),

    async setStatus(status) {
      if (!this.selectedContacts || this.selectedContacts.length === 0) {
        return;
      }

      let statusKey = status;
      if (typeof status === "object" && status !== null) {
        statusKey = Object.keys(status).find((key) => status[key]);
      }

      this.loadingBulkStatus = true;
      const updatePromises = this.selectedContacts.map((contactId) =>
        this.updateContactStatus(contactId, { status: statusKey })
          .then(() => ({ status: "fulfilled" }))
          .catch((err) => {
            this.$_notificationsMixin_handleCatch(err);
            return { status: "rejected", reason: err };
          })
      );
      const results = await Promise.allSettled(updatePromises);
      const fulfilled = results.filter((r) => r.status === "fulfilled").length;
      const rejected = results.filter((r) => r.status === "rejected").length;
      if (rejected > 0) {
        this.$_notificationsMixin_makeErrorToast(
          "Error",
          `${rejected} contact(s) failed to update status.`
        );
      }
      if (fulfilled > 0) {
        this.$_notificationsMixin_makeToast(
          "Status Update",
          `${fulfilled} contact(s) updated successfully`,
          "success"
        );
      }
      await this.fetchContacts();
      this.selectedMyContactsHandler();
      this.loadingBulkStatus = false;
    },

    async deleteSelectedContacts() {
      if (!this.selectedContacts || this.selectedContacts.length === 0) {
        return;
      }
      if (!window.confirm("Are you sure you want to delete these contacts?")) {
        return;
      }
      this.loadingBulkDelete = true;
      const deletePromises = this.selectedContacts.map((contactId) =>
        this.deleteContact(contactId)
          .then(() => ({ status: "fulfilled" }))
          .catch((err) => {
            this.$_notificationsMixin_handleCatch(err);
            return { status: "rejected", reason: err };
          })
      );
      const results = await Promise.allSettled(deletePromises);
      const fulfilled = results.filter((r) => r.status === "fulfilled").length;
      const rejected = results.filter((r) => r.status === "rejected").length;
      if (rejected > 0) {
        this.$_notificationsMixin_makeErrorToast(
          "Error",
          `${rejected} contact(s) failed to delete.`
        );
      }
      if (fulfilled > 0) {
        this.$_notificationsMixin_makeToast(
          "Delete Contacts",
          `${fulfilled} contact(s) deleted successfully`,
          "success"
        );
      }
      await this.fetchContacts();
      this.selectedMyContactsHandler();
      this.loadingBulkDelete = false;
    },
  },

  name: "select-bar",
};
</script>
